<template>
  <div class="store-details">
    <v-list two-line class="list">
      <v-list-item>
        <v-list-item-avatar>
          <v-img v-if="store.avatar" v-bind:src="store.avatar" />
          <v-avatar v-else color="grey" size="40">
            <span class="white--text headline">{{
              store.name.charAt(0).toUpperCase()
            }}</span>
          </v-avatar>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>{{ store.name }}</v-list-item-title>
          <v-list-item-subtitle>{{ store.categoryName }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn @click="toggleFavorite" icon>
            <v-icon v-if="isFavorite" color="red">mdi-heart</v-icon>
            <v-icon v-else>mdi-heart-outline</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <l-map
      :zoom="map.zoom"
      :center="store.location.latLng"
      :options="map.options"
      style="height: 35vh"
    >
      <l-tile-layer :url="map.url" :attribution="map.attribution" />
      <l-marker :lat-lng="store.location.latLng"></l-marker>
    </l-map>

    <v-container>
      <v-row>
        <v-col cols="10" offset="1" class="description">
          <img v-if="store.logo" class="logo" v-bind:src="store.logo" />
          <p v-html="store.description"></p>
          <div class="labels">
            <v-img
              class="label"
              v-for="label in store.labels"
              :key="label._id"
              :src="computeLabelLogoUrl(label._id)"
            />
          </div>
          <a v-bind:href="store.website" target="_blank">{{ store.website }}</a>
          <hr />
          <div class="address-wrapper">
            <p class="address">
              <span>{{ store.legalName }}</span>

              <br />
              <span>
                {{ store.location.street }} {{ store.location.houseNumber }}
              </span>

              <br />
              <span>
                {{ store.location.zipCode }} {{ store.location.city }}
              </span>

              <br v-if="store.phone" />
              <span v-if="store.phone">
                {{ store.phone }}
              </span>

              <br v-if="store.email" />
              <span v-if="store.email">
                {{ store.email }}
              </span>
            </p>
            <a
              class="maps-link"
              v-bind:href="store.location.googleMapsUrl"
              target="_blank"
            >
              <v-icon color="primary" large>mdi-directions</v-icon>
            </a>
          </div>
          <div class="opening-hours">
            <table v-if="typeof store.openingHours === 'object'">
              <tr>
                <td>Montag:</td>
                <td>{{ store.openingHours.monday }}</td>
              </tr>
              <tr>
                <td>Dienstag:</td>
                <td>{{ store.openingHours.tuesday }}</td>
              </tr>
              <tr>
                <td>Mittwoch:</td>
                <td>{{ store.openingHours.wednesday }}</td>
              </tr>
              <tr>
                <td>Donnerstag:</td>
                <td>{{ store.openingHours.thursday }}</td>
              </tr>
              <tr>
                <td>Freitag:</td>
                <td>{{ store.openingHours.friday }}</td>
              </tr>
              <tr>
                <td>Samstag:</td>
                <td>{{ store.openingHours.saturday }}</td>
              </tr>
              <tr>
                <td>Sonntag:</td>
                <td>{{ store.openingHours.sunday }}</td>
              </tr>
            </table>

            <p v-else>{{ store.openingHours }}</p>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<style lang="scss" scoped>
hr {
  margin: 1rem 0;
}

.logo {
  display: block;
  margin-bottom: 1.5rem;
  max-width: 100%;
  max-height: 5rem;
}

.labels {
  margin-bottom: 0.5rem;

  .label {
    display: inline-block;
    height: 2.5rem;
    width: 2.5rem;
    margin-right: 1rem;
  }
}

.description {
  text-align: justify;
  font-size: 0.9rem;

  .address,
  .opening-hours {
    font-weight: bold;
  }

  .address {
    float: left;
  }

  .maps-link {
    text-decoration: none;
    float: right;
    margin: 0.75rem 1rem 0 0;
  }

  .address-wrapper:after {
    content: "";
    display: table;
    clear: both;
  }

  .opening-hours {
    table {
      tr {
        td:first-of-type {
          padding-right: 0.5rem;
        }
      }
    }
  }
}
</style>

<script>
import favorites from "@/generics/favorites.js";

export default {
  name: "StoreDetails",

  props: ["storeData"],

  data: () => {
    return {
      map: {
        zoom: 15,
        url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution:
          '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        options: {
          zoomSnap: 0.5
        }
      },

      favList: favorites.get()
    };
  },

  computed: {
    store: function() {
      let generateGoogleMapsUrl = () => {
        if (this.storeData.location.googlePlaceId != null) {
          return encodeURI(
            `https://www.google.com/maps/dir/?api=1&` +
              `destination=${this.storeData.name}&` +
              `destination_place_id=${this.storeData.location.googlePlaceId}`
          );
        } else {
          return encodeURI(
            `https://www.google.com/maps/dir/?api=1&` +
              `destination=${this.storeData.name}, ` +
              `${this.storeData.location.street} ${this.storeData.location.houseNumber}, ` +
              `${this.storeData.location.zipCode} ${this.storeData.location.city}`
          );
        }
      };

      let openingTimeToString = time => {
        let hours = Math.floor(time / 60);
        let hoursStr = hours < 10 ? `0${hours}` : `${hours}`;

        let minutes = time % 60;
        let minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;

        return `${hoursStr}:${minutesStr}`;
      };

      let parseOpeningDay = day => {
        if (day.length === 0) {
          return "geschlossen";
        } else {
          let result = [];

          for (let time of day) {
            let start = openingTimeToString(time.from);
            let end = openingTimeToString(time.to);

            result.push(`${start} - ${end}`);
          }

          return result.join(", ");
        }
      };

      let result = {
        avatar: this.storeData.avatar,
        logo: this.storeData.logo,

        name: this.storeData.name,
        categoryName: this.storeData.categoryName,
        labels: this.storeData.labels,
        description: this.storeData.description.replace("\n", "<br />"),
        website: this.storeData.website,
        phone: this.storeData.phone,
        email: this.storeData.email,
        legalName: this.storeData.legalName,

        location: {
          latLng: this.storeData.location.latLng,
          street: this.storeData.location.street,
          houseNumber: this.storeData.location.houseNumber,
          zipCode: this.storeData.location.zipCode,
          city: this.storeData.location.city,
          googleMapsUrl: generateGoogleMapsUrl()
        }
      };

      if (typeof this.storeData.openingHours === "object") {
        result.openingHours = {
          monday: parseOpeningDay(this.storeData.openingHours.monday),
          tuesday: parseOpeningDay(this.storeData.openingHours.tuesday),
          wednesday: parseOpeningDay(this.storeData.openingHours.wednesday),
          thursday: parseOpeningDay(this.storeData.openingHours.thursday),
          friday: parseOpeningDay(this.storeData.openingHours.friday),
          saturday: parseOpeningDay(this.storeData.openingHours.saturday),
          sunday: parseOpeningDay(this.storeData.openingHours.sunday)
        };
      } else {
        result.openingHours = this.storeData.openingHours;
      }

      return result;
    },

    isFavorite: function() {
      return this.favList.includes(this.storeData._id);
    }
  },

  methods: {
    toggleFavorite: function() {
      if (this.isFavorite === true) {
        this.favList = favorites.remove(this.storeData._id);
      } else {
        this.favList = favorites.add(this.storeData._id);
      }
    },

    computeLabelLogoUrl: function(labelId) {
      return require(`@/assets/img/labels/${labelId}.png`);
    }
  }
};
</script>
